import type { DocumentSource } from '../models/viewer-types';
import type { PdfjsModule, PdfSource, PdfSourceOptions } from './pdf-types';

/** A source that can be read in parts: anything less cannot be handed to PDF.js as a range source. */
type RangedSource = DocumentSource & { readRange: NonNullable<DocumentSource['readRange']> };

/**
 * Hands a document-relative byte source to PDF.js, which then pulls the ranges it needs. What the
 * source reads from — an archive, a remote file — stays its own business and is never exposed.
 *
 * @param {RangedSource} source The source to read through.
 * @param {PdfjsModule} pdfjs The PDF.js module in use.
 * @param {PdfSourceOptions} options Limit, progress, and the way out.
 * @return {Promise<PdfSource>} The whole document when it is small, a range transport otherwise.
 */
export async function createRangedPDFSource(source: RangedSource, pdfjs: PdfjsModule, options: PdfSourceOptions): Promise<PdfSource> {
  const size = source.size;

  if (!Number.isSafeInteger(size) || size < 1 || size > options.maxFileBytes) {
    throw Error('The file exceeds the configured size limit.');
  }

  const received = new Map<number, number>();

  async function read(begin: number, end: number): Promise<Uint8Array> {
    const bytes = await source.readRange(begin, end - begin, options.signal);

    if (!(bytes instanceof Uint8Array) || bytes.length !== end - begin) {
      throw Error('The document source returned an invalid byte range.');
    }

    received.set(begin, Math.max(received.get(begin) || 0, end));

    // Ranges may overlap, so progress is what they cover together rather than what they add up to.
    let count = 0;
    let last = 0;

    for (const [start, stop] of [...received].sort((left, right) => left[0] - right[0])) {
      count += Math.max(0, stop - Math.max(start, last));
      last = Math.max(last, stop);
    }

    options.onProgress?.(count, size);

    return bytes.slice();
  }

  const initial = await read(0, Math.min(65536, size));

  if (initial.length === size) return { data: initial.slice(), size };

  class Transport extends pdfjs.PDFDataRangeTransport {
    override requestDataRange(begin: number, end: number): void {
      read(begin, end)
        .then((bytes) => {
          if (!options.signal.aborted) this.onDataRange(begin, bytes);
        })
        .catch((error: Error) => {
          if (!options.signal.aborted) {
            options.onError(error.name === 'AbortError' ? Error('The document source is no longer available.') : error);
          }
        });
    }

    override abort(): void {
      options.abort();
    }
  }

  return { range: new Transport(size, initial, true), length: size, size };
}
